<script lang="ts" setup>
/*=============================================================================*/
/*                                 Imports                                     */
/*=============================================================================*/
import { ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useForm } from "vee-validate"
import * as yup from "yup"
import { IrveFormBorne } from "@types"

type Props = {
    index: number
    borne: IrveFormBorne | null
}
const props = withDefaults(defineProps<Props>(), {
    value: null,
    index: 0,
})
const emit = defineEmits(["click:remove", "click:duplicate", "update:borne"])

/**================================================================*/
/*                         Formulaire
  ==================================================================*/
/**
 * Liste des puissances disponibles
 */
const powerList: number[] = [
    3.7, 7.4, 11, 22, 50, 75, 100, 150, 200, 250, 300, 360,
]
const schema = yup.object({
    power: yup.number().required("La puissance de la borne est requise"),
    powerFactor: yup
        .number()
        .min(0, "La valeur doit être comprise entre 0 et 1")
        .max(1, "La valeur doit être comprise entre 0 et 1")
        .required("Le facteur de puissance est requis"),
    powerMean: yup
        .number()
        .min(0, "La valeur doit être comprise entre 0 et 4000")
        .max(4000, "La valeur doit être comprise entre 0 et 4000")
        .required("La puissance moyenne est requise"),
    quantity: yup
        .number()
        .min(1, "La valeur doit être comprise entre 1 et 100")
        .max(100, "La valeur doit être comprise entre 1 et 100")
        .required("La quantité est requise"),
})
const { errors, defineField, validateField } = useForm({
    validationSchema: schema,
    initialValues: {
        power: props.borne?.power || null,
        powerFactor: props.borne?.powerFactor || null,
        powerMean: props.borne?.powerMean || null,
        quantity: props.borne?.quantity || 1,
    },
})

const [power] = defineField("power")
const [powerFactor] = defineField("powerFactor")
const [powerMean] = defineField("powerMean")
const [quantity] = defineField("quantity")

watch([power, powerFactor, powerMean, quantity], () => {
    emit("update:borne", {
        power: power.value,
        powerFactor: powerFactor.value,
        powerMean: powerMean.value,
        quantity: quantity.value,
    })
})

watch(
    () => props.borne,
    (borne) => {
        if (!borne) return
        power.value = borne?.power || null
        powerFactor.value = borne?.powerFactor || null
        powerMean.value = borne?.powerMean || null
        quantity.value = borne?.quantity || 1
    },
    {
        deep: true,
        immediate: true,
    }
)

/*=============================================================================*/
/*                                  Lang                                       */
/*=============================================================================*/
const { t } = useI18n({
    messages: {
        fr: {
            title: "Borne {index}",
            label: {
                power: "Puissance de la borne",
                power_factor: "Facteur de puissance - Cos φ (entre 0 et 1)",
                power_mean: "Puiss. moy. délivrée en charge",
                quantity:
                    "Nombre de bornes installées avec cette configuration",
            },
            tooltip: {
                power: "Puissance maximum théorique délivrée par la borne.",
                power_factor:
                    "Indicateur d’efficacité de la borne (rapport en puissance active et puissance apparente). Mettre 1 par défaut.",
                power_mean:
                    "Moyenne de la puissance fournie par la borne de recharge pendant la période de charge (cette puissance dépend du câble, du véhicule, des compteurs et de l’état de charge de la batterie).",
            },
            suffix: {
                power: "kVA",
                powerMean: "kVA",
            },
            hint: {
                quantity: "Nombre de borne installé avec cette configuration",
                power_factor:
                    "Le facteur de puissance est un nombre compris entre 0 et 1",
                power_mean:
                    "La puissance moyenne délivrée en charge doit être inférieure à la puissance de la borne",
            },
        },
    },
})

/*=============================================================================*/
/*                        Gestion du menu                                      */
/*=============================================================================*/
/**
 * Liste des actions du menu
 */
const items = ref([
    {
        label: "Dupliquer",
        icon: "pi pi-copy",
        command: () => emit("click:duplicate"),
    },
    {
        label: "Supprimer",
        icon: "pi pi-trash",
        command: () => emit("click:remove"),
    },
])

/**
 * Référence du menu (BaseMenu)
 */
const menu = ref()

defineExpose({
    validate: async () => {
        const { valid: validPower } = await validateField("power")
        const { valid: validPowerFactor } = await validateField("powerFactor")
        const { valid: validPowerMean } = await validateField("powerMean")
        const { valid: validQuantity } = await validateField("quantity")

        return validPower && validPowerFactor && validPowerMean && validQuantity
    },
})
</script>
<template>
    <div class="borne-row flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
            <h1 class="font-bold text-xl text-primary-700">
                {{ t("title") + index.toString() }}
            </h1>
            <!-- Menu -->
            <div class="flex shrink-0">
                <BaseButton
                    type="button"
                    icon="pi pi-ellipsis-h"
                    @click="menu.toggle($event)"
                    rounded
                    text
                    aria-haspopup="true"
                    aria-controls="overlay_menu"
                />
                <BaseMenu
                    ref="menu"
                    id="overlay_menu"
                    :model="items"
                    :popup="true"
                />
            </div>
        </div>
        <form novalidate class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Puissance -->
            <BaseInputWrapper
                :label="t('label.power')"
                input-id="power"
                :suffix="t('suffix.power')"
                :error="errors.power"
                unfloat
                v-tooltip="t('tooltip.power')"
            >
                <BaseSelect
                    v-model="power"
                    input-id="power"
                    class="w-full"
                    :options="powerList"
                    :invalid="!!errors.power"
                    @focus="
                        emit('update:borne', {
                            power: power,
                            powerFactor: powerFactor,
                            powerMean: powerMean,
                            quantity: quantity,
                        })
                    "
                />
            </BaseInputWrapper>

            <!-- Facteur de puissance -->
            <BaseInputWrapper
                input-id="power-factor"
                :label="t('label.power_factor')"
                :error="errors.powerFactor"
                unfloat
                :hint="t('hint.power_factor')"
            >
                <BaseInputNumber
                    v-model="powerFactor"
                    input-id="power-factor"
                    class="w-full"
                    :invalid="!!errors.powerFactor"
                    :disabled="!power"
                    :step="0.001"
                    :min-fraction-digits="0"
                    :max-fraction-digits="3"
                    locale="fr-FR"
                    :max="1"
                    :min="0"
                />
            </BaseInputWrapper>

            <!-- Puissance moyenne -->
            <BaseInputWrapper
                input-id="power-mean"
                :label="t('label.power_mean')"
                :suffix="t('suffix.powerMean')"
                :error="errors.powerMean"
                unfloat
                :hint="t('hint.power_mean')"
            >
                <BaseInputNumber
                    v-model="powerMean"
                    input-id="power-mean"
                    class="w-full"
                    :invalid="!!errors.powerMean"
                    :step="0.01"
                    :min-fraction-digits="0"
                    :max-fraction-digits="3"
                    :disabled="!power"
                    locale="fr-FR"
                    :max="power"
                    :min="0"
                />
            </BaseInputWrapper>

            <!-- Quantité -->
            <BaseInputWrapper
                input-id="quantity"
                :label="t('label.quantity')"
                unfloat
                :error="errors.quantity"
            >
                <BaseInputNumber
                    v-model="quantity"
                    input-id="quantity"
                    :disabled="!power"
                    :min="0"
                    class="w-full"
                    :invalid="!!errors.quantity"
                />
            </BaseInputWrapper>
        </form>
    </div>
</template>
